import { DateTime } from 'luxon';
import { usageProvenance, type UsageCaptureContext } from '../../onboarding-sessions/usage-config';
import {
  BASELINE_CANONICAL_WEEK_DAYS,
  baselineSubstanceTypesDetailsSchema,
  type BaselineKind,
  type BaselineUnit,
  type CanonicalWeek,
  type CanonicalWeekCore,
  type CanonicalWeekEntry,
} from '../schema';

// Canonical baseline fixtures: the one place the "canonical week a baseline
// write leaves behind" is written down for tests. Every fixture is paired with
// the SOURCE it comes from (an onboarding intake's answers, or a Pelago 1.0
// header and its day rows), and the baselines module's drift test runs each
// source through the real mapper, writer stamp and canonical-week builder and
// requires the result to equal the fixture exactly. Billing, voice, the
// baseline prompt phrase and Atlas build their weeks here and nowhere else, so a change
// to the stored shape breaks one fixture visibly instead of letting a reader
// drift silently. Every week must parse against `canonicalWeekSchema` (see
// fixtures.test.ts). Synthetic values only.

// ─── sources ────────────────────────────────────────────────────────────────

/** Day answers by ordinal: index 0 is `day_1` (the most recent day). Null leaves the day unanswered. */
export type BaselineDayAnswers = ReadonlyArray<number | null>;

/** An onboarding intake's baseline answers and the facts of its session. */
export interface OnboardingBaselineSource {
  /** The intake's catalogue programme key; it decides the unit. */
  programKey: string;
  usage?: UsageCaptureContext;
  /** The `week_anchor_date` answer, or null when the intake carries no usable one. */
  weekAnchorDate: string | null;
  /** The member-local completion day, the capture day when there is no anchor. */
  completedOn: string;
  /** The `week_typical` answer. */
  weekTypical: 'yes' | 'no' | null;
  /** `day_1..day_7`. */
  reported: BaselineDayAnswers;
  /** `typical_day_1..typical_day_7`. */
  typical: BaselineDayAnswers;
}

/** The 1.0 baseline tables, by the child-row array each arrives in. */
export type LegacyBaselineFixtureSubstance = 'alcohol' | 'cannabis' | 'opioid' | 'stimulant';

/** One 1.0 baseline day row, as the 1.0 import delivers it. */
export interface LegacyBaselineFixtureDay {
  /** `YYYY-MM-DD`. */
  date: string;
  abstained: boolean;
  standardDrinks?: number | null;
  standardUnits?: number | null;
  /** JSON-in-text array of substance types. */
  types?: string | null;
}

/** A member's current 1.0 baseline header and its rows for one substance. */
export interface LegacyBaselineSource {
  header: { id: number; baselineDate: string; created: string; modified: string | null };
  substance: LegacyBaselineFixtureSubstance;
  /** Rows under `header`, in any order. */
  rows: ReadonlyArray<LegacyBaselineFixtureDay>;
}

export type BaselineFixtureSource =
  | { origin: 'onboarding'; onboarding: OnboardingBaselineSource }
  | { origin: 'legacy_1_0'; legacy: LegacyBaselineSource };

export interface CanonicalBaselineFixture {
  source: BaselineFixtureSource;
  /** The designated canonical week the source produces, as the baselines read returns it. */
  week: CanonicalWeek;
}

// ─── shared rules ───────────────────────────────────────────────────────────

/** The id a fixture week carries; the drift test mints the same id for each kind. */
export function fixtureBaselineId(kind: BaselineKind): string {
  return `fixture-${kind}`;
}

const ONBOARDING_UNIT_BY_PROGRAMME: Readonly<Record<string, BaselineUnit>> = {
  alcohol: 'standard_drink',
  cannabis: 'cannabis_block',
};

const LEGACY_RULES: Readonly<
  Record<
    LegacyBaselineFixtureSubstance,
    {
      programKey: string;
      unit: BaselineUnit;
      quantityField: 'standardDrinks' | 'standardUnits' | null;
      carriesTypes: boolean;
    }
  >
> = {
  alcohol: {
    programKey: 'alcohol',
    unit: 'standard_drink',
    quantityField: 'standardDrinks',
    carriesTypes: false,
  },
  cannabis: {
    programKey: 'cannabis',
    unit: 'cannabis_standard_unit_1_0',
    quantityField: 'standardUnits',
    carriesTypes: false,
  },
  opioid: { programKey: 'opioid', unit: 'use', quantityField: null, carriesTypes: true },
  stimulant: { programKey: 'stimulants', unit: 'use', quantityField: null, carriesTypes: true },
};

/** The catalogue programme key a 1.0 substance's baseline lands on. */
export function legacyFixtureProgramKey(substance: LegacyBaselineFixtureSubstance): string {
  return LEGACY_RULES[substance].programKey;
}

function calendarDay(iso: string): DateTime {
  return DateTime.fromISO(iso, { zone: 'utc' });
}

function minusDays(iso: string, days: number): string {
  const date = calendarDay(iso).minus({ days }).toISODate();
  if (date === null) throw new Error(`not a calendar date: ${iso}`);
  return date;
}

function core(
  kind: BaselineKind,
  base: Pick<CanonicalWeekCore, 'source' | 'representativeness' | 'capturedOn'>,
  unit: BaselineUnit,
  entries: CanonicalWeekEntry[],
): CanonicalWeekCore {
  const sorted = [...entries].sort((a, b) => a.windowDay - b.windowDay);
  const complete = sorted.length === BASELINE_CANONICAL_WEEK_DAYS;
  return {
    baselineId: fixtureBaselineId(kind),
    kind,
    ...base,
    unit: sorted.length > 0 ? unit : null,
    complete,
    allUnused: complete && sorted.every((entry) => !entry.used),
    entries: sorted,
  };
}

/** The answered ordinals, `day_1` first. */
function answered(days: BaselineDayAnswers): Array<{ ordinal: number; quantity: number }> {
  return days.flatMap((quantity, i) => (quantity === null ? [] : [{ ordinal: i + 1, quantity }]));
}

// ─── onboarding ─────────────────────────────────────────────────────────────

/** An onboarding source with defaults: an anchored alcohol intake and no answers. */
export function onboardingBaselineSource(
  overrides: Partial<OnboardingBaselineSource> = {},
): OnboardingBaselineSource {
  return {
    programKey: 'alcohol',
    weekAnchorDate: '2026-10-15',
    completedOn: '2026-10-15',
    weekTypical: null,
    reported: [],
    typical: [],
    ...overrides,
  };
}

/** The flat answer keys the intake persists for a source. */
export function onboardingBaselineAnswers(
  source: OnboardingBaselineSource,
): Record<string, unknown> {
  const answers: Record<string, unknown> = {};
  source.reported.forEach((quantity, i) => {
    if (quantity !== null) answers[`day_${i + 1}`] = quantity;
  });
  source.typical.forEach((quantity, i) => {
    if (quantity !== null) answers[`typical_day_${i + 1}`] = quantity;
  });
  if (source.weekTypical !== null) answers.week_typical = source.weekTypical;
  if (source.weekAnchorDate !== null) answers.week_anchor_date = source.weekAnchorDate;
  if (source.usage?.captureMode === 'yes-no') {
    answers.usage_capture = usageProvenance(
      source.usage,
      source.weekAnchorDate ?? source.completedOn,
    );
    source.reported.forEach((value, i) => {
      if (value !== null) answers[`binary_day_${i + 1}`] = String(value);
    });
    source.typical.forEach((value, i) => {
      if (value !== null) answers[`typical_binary_day_${i + 1}`] = String(value);
    });
  }
  return answers;
}

/** Every week one intake writes, each as the canonical-week builder reads it, designated or not. */
export interface OnboardingCapture {
  /** The reported week, when any reported day is valid. */
  reported: CanonicalWeekCore | null;
  /** The typical week, when the member said the reported week was unusual or gave only usual days. */
  typical: CanonicalWeekCore | null;
  /** The designated week as the baselines read returns it, or null when the intake designates none. */
  designated: CanonicalWeek | null;
}

/**
 * The weeks an onboarding intake writes. A reported week is dated back from
 * its capture day and ranked by recency; a typical week puts `typical_day_N`
 * on the weekday of the capture day minus N and is written, and designated,
 * when the member said the reported week was not typical or gave only usual
 * days, with the reported week (if any) beside it. A reported week is
 * designated only when it is dated and the member did not call it unusual, so
 * an undated week, or an unusual one with no usable usual week, is written
 * and designates nothing.
 */
export function onboardingCapture(source: OnboardingBaselineSource): OnboardingCapture {
  const reported = answered(source.reported);
  const typical = answered(source.typical);
  const writeTypical = typical.length > 0 && (source.weekTypical === 'no' || reported.length === 0);
  const anchored = source.weekAnchorDate !== null;
  const capturedOn = source.weekAnchorDate ?? source.completedOn;
  const binary = source.usage?.captureMode === 'yes-no';
  const provenance = source.usage ? usageProvenance(source.usage, capturedOn) : null;
  const capture =
    binary && provenance ? { ...provenance, captureMode: 'yes-no' as const } : undefined;
  const unit = binary ? 'use' : (ONBOARDING_UNIT_BY_PROGRAMME[source.programKey] ?? 'use');
  const entry = (
    quantity: number,
    at: Omit<CanonicalWeekEntry, 'used' | 'quantity' | 'details'>,
  ) => ({
    ...at,
    used: quantity > 0,
    quantity: binary ? null : quantity,
    details: null,
  });

  const rawReportedCore =
    reported.length === 0
      ? null
      : core(
          'reported_week',
          {
            source: 'onboarding',
            capturedOn,
            representativeness: !anchored
              ? 'unknown'
              : source.weekTypical === 'yes'
                ? 'confirmed'
                : source.weekTypical === 'no'
                  ? 'not_representative'
                  : 'unknown',
          },
          unit,
          // Every answered day lies inside the seven days before capture, so
          // the stamp ranks them by recency: the lowest ordinal is window day 1.
          reported.map(({ ordinal, quantity }, rank) =>
            entry(quantity, {
              windowDay: rank + 1,
              entryDate: minusDays(capturedOn, ordinal),
              weekday: null,
            }),
          ),
        );

  const rawTypicalCore = writeTypical
    ? core(
        'typical_week',
        { source: 'onboarding', capturedOn, representativeness: 'confirmed' },
        unit,
        typical.map(({ ordinal, quantity }) =>
          entry(quantity, {
            windowDay: ordinal,
            entryDate: null,
            weekday: calendarDay(capturedOn).minus({ days: ordinal }).weekday,
          }),
        ),
      )
    : null;

  const reportedCore =
    rawReportedCore && capture ? { ...rawReportedCore, capture } : rawReportedCore;
  const typicalCore = rawTypicalCore && capture ? { ...rawTypicalCore, capture } : rawTypicalCore;
  const designated =
    typicalCore !== null
      ? { ...typicalCore, reportedWeek: reportedCore }
      : reportedCore !== null && anchored && source.weekTypical !== 'no'
        ? { ...reportedCore, reportedWeek: null }
        : null;
  return { reported: reportedCore, typical: typicalCore, designated };
}

/**
 * Whether some week the intake writes holds all seven canonical-week days,
 * designated or not: the fact billing's capture gate reads for a member whose
 * only baselines are this intake's.
 */
export function onboardingCaptureIsComplete(source: OnboardingBaselineSource): boolean {
  const { reported, typical } = onboardingCapture(source);
  return reported?.complete === true || typical?.complete === true;
}

/**
 * The designated canonical week an onboarding intake produces. Throws for a
 * source that designates nothing, so a fixture cannot describe an impossible
 * week; {@link onboardingCapture} describes such an intake.
 */
export function onboardingCanonicalWeek(source: OnboardingBaselineSource): CanonicalWeek {
  const { designated } = onboardingCapture(source);
  if (designated === null) throw new Error('this intake designates no baseline');
  return designated;
}

function onboardingFixture(source: OnboardingBaselineSource): CanonicalBaselineFixture {
  return {
    source: { origin: 'onboarding', onboarding: source },
    week: onboardingCanonicalWeek(source),
  };
}

// ─── Pelago 1.0 ─────────────────────────────────────────────────────────────

/** The 1.0 window spans the baseline date and the 29 days before it. */
const LEGACY_WINDOW_DAYS = 30;

function legacyTypes(raw: string | null | undefined): CanonicalWeekEntry['details'] {
  if (typeof raw !== 'string') return null;
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = baselineSubstanceTypesDetailsSchema.safeParse({
    kind: 'substance_types',
    types: decoded,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * The designated canonical week a 1.0 baseline produces: the seven most recent
 * rows dated inside the 30-day window ending on the baseline date (inclusive),
 * ranked by recency. An abstained day is a reported zero; a used day keeps the
 * quantity 1.0 delivered; opioid and stimulant rows carry use and types only.
 */
export function legacyCanonicalWeek(source: LegacyBaselineSource): CanonicalWeek {
  const rule = LEGACY_RULES[source.substance];
  const anchor = source.header.baselineDate;
  const earliest = minusDays(anchor, LEGACY_WINDOW_DAYS - 1);
  const inWindow = source.rows
    .filter((row) => row.date >= earliest && row.date <= anchor)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    .slice(0, BASELINE_CANONICAL_WEEK_DAYS);
  const entries = inWindow.map((row, rank): CanonicalWeekEntry => {
    const delivered = rule.quantityField === null ? null : (row[rule.quantityField] ?? null);
    return {
      windowDay: rank + 1,
      entryDate: row.date,
      weekday: null,
      used: !row.abstained,
      quantity: rule.quantityField === null ? null : row.abstained ? 0 : delivered,
      details: rule.carriesTypes ? legacyTypes(row.types) : null,
    };
  });
  return {
    ...core(
      'legacy_window',
      { source: 'legacy_1_0', representativeness: 'unknown', capturedOn: anchor },
      rule.unit,
      entries,
    ),
    reportedWeek: null,
  };
}

/** A 1.0 source with one header, created on its baseline date. */
export function legacyBaselineSource(
  substance: LegacyBaselineFixtureSubstance,
  baselineDate: string,
  rows: ReadonlyArray<LegacyBaselineFixtureDay>,
): LegacyBaselineSource {
  return {
    header: { id: 101, baselineDate, created: `${baselineDate} 09:00:00`, modified: null },
    substance,
    rows,
  };
}

/** One 1.0 row as a reader thinks of it: whether the member used, and the count when kept. */
export interface LegacyBaselineFixtureUse {
  used: boolean;
  quantity: number | null;
}

/**
 * 1.0 rows for a run of days, newest first and `gapDays` apart starting on the
 * baseline date. A null leaves that day without a row.
 */
export function legacyBaselineRows(
  substance: LegacyBaselineFixtureSubstance,
  baselineDate: string,
  days: ReadonlyArray<LegacyBaselineFixtureUse | null>,
  gapDays = 1,
): LegacyBaselineFixtureDay[] {
  const field = LEGACY_RULES[substance].quantityField;
  return days.flatMap((day, i) => {
    if (day === null) return [];
    const row: LegacyBaselineFixtureDay = {
      date: minusDays(baselineDate, i * gapDays),
      abstained: !day.used,
    };
    if (field !== null) row[field] = day.quantity;
    return [row];
  });
}

function legacyFixture(source: LegacyBaselineSource): CanonicalBaselineFixture {
  return { source: { origin: 'legacy_1_0', legacy: source }, week: legacyCanonicalWeek(source) };
}

// ─── the canonical fixtures ─────────────────────────────────────────────────

/** A native cannabis week the member confirmed as typical. */
export const NATIVE_CONFIRMED_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    programKey: 'cannabis',
    weekTypical: 'yes',
    reported: [2, 1, 0, 0, 3, 0, 0],
  }),
);

/**
 * A member whose reported week was unusual: the typical week they gave is
 * designated and the reported week rides beside it, not representative.
 */
export const ATYPICAL_TYPICAL_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    weekAnchorDate: '2026-08-19',
    completedOn: '2026-08-19',
    weekTypical: 'no',
    typical: [3, 1, 4, 3, 2, 4, 4],
    reported: [4, 0, 6, 2, 0, 3, 5],
  }),
);

/** A member who gave only usual days: a typical week with no reported week beside it. */
export const TYPICAL_ONLY_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    weekAnchorDate: '2026-07-27',
    completedOn: '2026-07-27',
    weekTypical: 'yes',
    typical: [2],
  }),
);

/** A reported week with unanswered days: incomplete, the gaps closed up by recency. */
export const GAPPED_REPORTED_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    weekTypical: 'yes',
    reported: [2, 0, null, 1, null, null, null],
  }),
);

/** Zero use on every day of a week the member confirmed was typical. */
export const ALL_ZERO_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    weekAnchorDate: '2026-08-04',
    completedOn: '2026-08-04',
    weekTypical: 'yes',
    reported: [0, 0, 0, 0, 0, 0, 0],
  }),
);

/** A reported week nobody asked about: `week_typical` was never answered. */
export const UNKNOWN_REPRESENTATIVENESS_BASELINE = onboardingFixture(
  onboardingBaselineSource({
    weekAnchorDate: '2026-07-27',
    completedOn: '2026-07-27',
    reported: [2, 0, 1, 0, 4, 6, 5],
  }),
);

/** A complete 1.0 alcohol window: use on 5 of 7 days, 23.5 standard drinks. */
export const LEGACY_ALCOHOL_BASELINE = legacyFixture(
  legacyBaselineSource(
    'alcohol',
    '2024-03-10',
    legacyBaselineRows('alcohol', '2024-03-10', [
      { used: true, quantity: 6 },
      { used: true, quantity: 4.5 },
      { used: false, quantity: 0 },
      { used: true, quantity: 5 },
      { used: true, quantity: 3 },
      { used: false, quantity: 0 },
      { used: true, quantity: 5 },
    ]),
  ),
);

/** A 1.0 cannabis week of fractional standard units, every day of use. */
export const LEGACY_CANNABIS_FRACTIONAL_BASELINE = legacyFixture(
  legacyBaselineSource(
    'cannabis',
    '2025-11-30',
    legacyBaselineRows(
      'cannabis',
      '2025-11-30',
      [0.4, 0.25, 0.4, 1.5, 0.4, 0.5, 0.4].map((quantity) => ({ used: true, quantity })),
    ),
  ),
);

/** A 1.0 opioid week: use recorded with no count and a type list, and days of none. */
export const LEGACY_OPIOID_USE_ONLY_BASELINE = legacyFixture(
  legacyBaselineSource(
    'opioid',
    '2024-03-10',
    legacyBaselineRows('opioid', '2024-03-10', [
      { used: true, quantity: null },
      { used: false, quantity: null },
      { used: true, quantity: null },
      { used: true, quantity: null },
      { used: false, quantity: null },
      { used: false, quantity: null },
      { used: true, quantity: null },
    ]).map((row) => (row.abstained ? row : { ...row, types: '["Fentanyl"]' })),
  ),
);

/** Every canonical fixture, by name, for the drift guards. */
export const CANONICAL_BASELINE_FIXTURES: Readonly<Record<string, CanonicalBaselineFixture>> = {
  NATIVE_CONFIRMED_BASELINE,
  ATYPICAL_TYPICAL_BASELINE,
  TYPICAL_ONLY_BASELINE,
  GAPPED_REPORTED_BASELINE,
  ALL_ZERO_BASELINE,
  UNKNOWN_REPRESENTATIVENESS_BASELINE,
  LEGACY_ALCOHOL_BASELINE,
  LEGACY_CANNABIS_FRACTIONAL_BASELINE,
  LEGACY_OPIOID_USE_ONLY_BASELINE,
};

/**
 * Intakes billing's capture gate is decided on, by shape. The first three
 * report seven days yet designate nothing complete; the fourth designates a
 * complete usual week beside a sparse reported one; the last never gives
 * seven days. The drift test runs each through the real write path.
 */
export const INTAKE_CAPTURE_SHAPES: Readonly<Record<string, OnboardingBaselineSource>> = {
  /** Seven reported days with no usable anchor and no usual week: written undated, undesignated. */
  UNDATED_FULL_REPORTED_WEEK: onboardingBaselineSource({
    weekAnchorDate: null,
    completedOn: '2026-08-04',
    reported: [0, 2, 0, 1, 0, 0, 3],
  }),
  /** Seven reported days called unusual, beside an incomplete usual week, which is designated. */
  UNUSUAL_FULL_WEEK_GAPPED_TYPICAL: onboardingBaselineSource({
    weekAnchorDate: '2026-08-04',
    completedOn: '2026-08-04',
    weekTypical: 'no',
    reported: [1, 0, 2, 0, 0, 4, 1],
    typical: [2, 2, null, 1],
  }),
  /** Seven reported days called unusual with no usable usual week: written, undesignated. */
  UNUSUAL_FULL_WEEK_NO_TYPICAL: onboardingBaselineSource({
    weekAnchorDate: '2026-08-04',
    completedOn: '2026-08-04',
    weekTypical: 'no',
    reported: [0, 0, 3, 0, 1, 0, 0],
  }),
  /** Fewer than seven reported days called unusual, beside a complete designated usual week. */
  SPARSE_REPORTED_FULL_TYPICAL: onboardingBaselineSource({
    weekAnchorDate: '2026-08-04',
    completedOn: '2026-08-04',
    weekTypical: 'no',
    reported: [2, null, 1],
    typical: [1, 0, 2, 0, 0, 3, 1],
  }),
  /** A typical reported week with days left unanswered and no usual week: never seven days. */
  INCOMPLETE_INTAKE: onboardingBaselineSource({
    weekAnchorDate: '2026-08-04',
    completedOn: '2026-08-04',
    weekTypical: 'yes',
    reported: [0, 1, null, 0, 2, null, 0],
  }),
};

/**
 * Further sources the readers build weeks from through the builders above
 * (anchorless intakes, gaps, Friday anchors, sparse 1.0 windows, out-of-window
 * rows). The drift test runs these through the real path too, so a builder
 * cannot pass for the named fixtures alone and drift on the rest.
 */
export const BASELINE_BUILDER_DRIFT_SOURCES: ReadonlyArray<BaselineFixtureSource> = [
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({
      weekAnchorDate: '2026-07-24',
      completedOn: '2026-07-24',
      reported: [2, 0, 1, 0, 4, 6, 5],
    }),
  },
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({
      weekAnchorDate: null,
      completedOn: '2026-07-27',
      weekTypical: 'no',
      typical: [1, 2],
      reported: [0, 3],
    }),
  },
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({
      weekAnchorDate: '2026-07-27',
      completedOn: '2026-07-29',
      typical: [1, null, null, null, null, 4],
    }),
  },
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({
      programKey: 'tobacco',
      weekTypical: 'yes',
      reported: [null, null, null, 0, null, null, null],
    }),
  },
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({
      weekTypical: 'no',
      typical: [0, 0, 0, 0, 0, 0],
      reported: [0, 0, null, 0, 0, 0, 0],
    }),
  },
  {
    origin: 'onboarding',
    onboarding: onboardingBaselineSource({ reported: [2.7, 0.1, null, 0.2] }),
  },
  {
    origin: 'legacy_1_0',
    legacy: legacyBaselineSource(
      'alcohol',
      '2025-11-30',
      legacyBaselineRows(
        'alcohol',
        '2025-11-30',
        [4, 0, 6, 2, 0, 3, 5, 7, 1].map((q) => ({ used: q > 0, quantity: q })),
        3,
      ),
    ),
  },
  {
    origin: 'legacy_1_0',
    legacy: legacyBaselineSource('cannabis', '2025-11-30', [
      { date: '2025-11-30', abstained: false, standardUnits: 0 },
      { date: '2025-11-29', abstained: false, standardUnits: null },
      { date: '2025-10-01', abstained: false, standardUnits: 2 },
      { date: '2025-12-01', abstained: false, standardUnits: 2 },
    ]),
  },
  {
    origin: 'legacy_1_0',
    legacy: legacyBaselineSource('stimulant', '2025-11-30', [
      { date: '2025-11-30', abstained: false, types: '["Cocaine","Meth"]' },
      { date: '2025-11-28', abstained: true, types: 'not json' },
    ]),
  },
];
